// pipelineArc — pure derivation of the landing hero's authoring step-indicator.
//
// The home shows an OutcomeArc as a "where am I in the journey" scent:
// Source → Ask → Material → Package. Its node states are a FOLD over the CURRENT
// TASK (the nascent conversation), never over the whole workspace inventory
// (F-D / DB2): a workspace that already holds a material must NOT light the arc
// on a brand-new "what do you want to create today?" task. The caller (HeroHost)
// computes the snapshot from task-scoped signals — the sources grounded into
// THIS conversation, and its transcript — so the arc is honest to the task, not
// the library. The first not-done node is the single `current` invitation;
// earlier nodes are `done`, later ones `upcoming`. Pure — no I/O, no Date, no
// mutation; every enumeration a named union, and the visible label + optional
// per-step explanation are caller concerns (localised copy) so no English is
// embedded here.

import type { IconName } from "../Icon/Icon.js";
import type { ArcStep, ArcStepState } from "../OutcomeArc/OutcomeArc.js";

// The pipeline this arc walks. Domain-specific to the authoring spine; the
// reusable ArcStep carries only an opaque `id`, so this union maps into `id`.
export type PipelineStepKind = "source" | "ask" | "material" | "package";

// The folded snapshot — the ONLY place "where this task stands" becomes
// progress. Each bit is a pure membership test the caller computes from the
// CURRENT task's signals (the sources grounded into this conversation, its
// transcript). `hasAsk` and `hasMaterial` are DISTINCT bits so one signal never
// lights two nodes (F-D): `hasAsk` = the author has asked / a material is being
// built (asking is under way), `hasMaterial` = a material actually exists.
// `hasPackage` has no task-scoped signal yet, so on a nascent task it rests
// false and its node stays `upcoming` — that IS the next step (Dm3, a stated
// shortcut until per-conversation package links exist).
export type PipelineProgress = {
    readonly hasSource: boolean;
    readonly hasAsk: boolean;
    readonly hasMaterial: boolean;
    readonly hasPackage: boolean;
};

// One node spec: its kind, its glyph, and whether the snapshot marks it done.
// `ask` is done once the author has asked OR a material exists (a material
// implies the ask that produced it), so it reads `current` exactly in the window
// where a source is present but nothing has been asked yet — the bridge moment
// the arc most wants to nudge.
type StepSpec = {
    readonly kind: PipelineStepKind;
    readonly icon: IconName;
    readonly done: (progress: PipelineProgress) => boolean;
};

const PIPELINE_SPEC: readonly StepSpec[] = [
    { kind: "source", icon: "source", done: (p) => p.hasSource },
    { kind: "ask", icon: "sparkle", done: (p) => p.hasAsk || p.hasMaterial },
    { kind: "material", icon: "material", done: (p) => p.hasMaterial },
    { kind: "package", icon: "export", done: (p) => p.hasPackage },
];

// Derive the four arc nodes from a progress snapshot. `labelFor` is totalled
// over the kind union by the caller (it holds the i18n `t`), keeping this
// derivation free of any locale. `explainFor` is optional per-step disclosure
// copy (Dm4) — also a caller concern; absent ⇒ no explanation attached.
export const derivePipelineArc = (
    progress: PipelineProgress,
    labelFor: (kind: PipelineStepKind) => string,
    explainFor?: (kind: PipelineStepKind) => string,
): readonly ArcStep[] => {
    const firstOpen = PIPELINE_SPEC.findIndex((spec) => !spec.done(progress));
    return PIPELINE_SPEC.map((spec, index) => {
        const state: ArcStepState = spec.done(progress)
            ? "done"
            : index === firstOpen
              ? "current"
              : "upcoming";
        return {
            id: spec.kind,
            label: labelFor(spec.kind),
            icon: spec.icon,
            state,
            ...(explainFor === undefined
                ? {}
                : { explanation: explainFor(spec.kind) }),
        };
    });
};
